/**
 * Cienki klient HTTP. Sesja siedzi w ciasteczku httpOnly, więc nie ma tu
 * żadnego zarządzania tokenami — wystarczy credentials: 'include'.
 */
export type KodBledu =
  | 'BLAD_WALIDACJI' | 'NIEZALOGOWANY' | 'BRAK_UPRAWNIEN' | 'NIE_ZNALEZIONO'
  | 'KONFLIKT_WERSJI' | 'KONFLIKT' | 'ZLE_DANE_LOGOWANIA' | 'KONTO_ZABLOKOWANE'
  | 'KONTO_NIEAKTYWNE' | 'WYMAGANA_ZMIANA_HASLA' | 'ZA_DUZO_PROB' | 'BLAD_SERWERA';

export class BladApi extends Error {
  constructor(
    readonly kod: KodBledu,
    komunikat: string,
    readonly status: number,
    readonly szczegoly?: { pola?: Record<string, string>; [k: string]: unknown },
  ) {
    super(komunikat);
    this.name = 'BladApi';
  }

  /** Komunikaty przy konkretnych polach formularza. */
  get bledyPol(): Record<string, string> {
    return (this.szczegoly?.pola as Record<string, string>) ?? {};
  }
}

async function zadanie<T>(metoda: string, sciezka: string, dane?: unknown): Promise<T> {
  let odp: Response;
  try {
    odp = await fetch(`/api${sciezka}`, {
      method: metoda,
      credentials: 'include',
      headers: dane ? { 'Content-Type': 'application/json' } : undefined,
      body: dane ? JSON.stringify(dane) : undefined,
    });
  } catch {
    throw new BladApi(
      'BLAD_SERWERA',
      'Brak łączności z serwerem. Sprawdź połączenie z siecią uczelnianą.',
      0,
    );
  }

  if (odp.status === 204) return undefined as T;

  const tresc = await odp.text();
  const cialo = tresc ? (JSON.parse(tresc) as Record<string, unknown>) : {};

  if (!odp.ok) {
    throw new BladApi(
      (cialo.kod as KodBledu) ?? 'BLAD_SERWERA',
      (cialo.komunikat as string) ?? 'Operacja się nie powiodła.',
      odp.status,
      cialo.szczegoly as BladApi['szczegoly'],
    );
  }
  return cialo as T;
}

/**
 * Pobranie pliku (XLSX, PDF). Odpowiedź jest binarna, więc nie przechodzi
 * przez `zadanie` — ale błędy serwera nadal przychodzą jako JSON i chcemy
 * pokazać ten sam komunikat, co wszędzie indziej.
 */
export async function pobierzPlik(sciezka: string, nazwaZapasowa: string): Promise<void> {
  const odp = await fetch(`/api${sciezka}`, { credentials: 'include' });

  if (!odp.ok) {
    const tresc = await odp.text();
    const cialo = tresc ? (JSON.parse(tresc) as Record<string, unknown>) : {};
    throw new BladApi(
      (cialo.kod as KodBledu) ?? 'BLAD_SERWERA',
      (cialo.komunikat as string) ?? 'Nie udało się pobrać pliku.',
      odp.status,
      cialo.szczegoly as BladApi['szczegoly'],
    );
  }

  // Nazwę nadaje serwer w Content-Disposition — trzymamy jedną prawdę.
  const naglowek = odp.headers.get('content-disposition') ?? '';
  const dopasowanie = /filename="([^"]+)"/.exec(naglowek);

  const adres = URL.createObjectURL(await odp.blob());
  const odnosnik = document.createElement('a');
  odnosnik.href = adres;
  odnosnik.download = dopasowanie?.[1] ?? nazwaZapasowa;
  document.body.appendChild(odnosnik);
  odnosnik.click();
  odnosnik.remove();
  URL.revokeObjectURL(adres);
}

export const api = {
  get: <T>(s: string) => zadanie<T>('GET', s),
  post: <T>(s: string, d?: unknown) => zadanie<T>('POST', s, d),
  put: <T>(s: string, d?: unknown) => zadanie<T>('PUT', s, d),
  del: <T>(s: string) => zadanie<T>('DELETE', s),
};
